import { createRoot } from 'react-dom/client'
import './index.css'
import { indexedDBManager } from './lib/indexedDB'
import * as serviceWorker from './lib/serviceWorker'
import { initClientSecurity } from './lib/clientSecurity'
import { initializeSentry } from './lib/sentry'
import { initLogStore } from './lib/logger'

// Install console interceptors first so every subsequent log is captured
initLogStore();

console.log('main.tsx: Starting application');

// Initialize Sentry as early as possible (only in production)
initializeSentry();

// Hide boot banner once we get this far
const bootBanner = document.getElementById('boot-banner');
if (bootBanner) {
  bootBanner.style.display = 'none';
}

// Initialize IndexedDB early with quota management
const initializeApp = async () => {
  try {
    console.log('main.tsx: Initializing security measures...');
    initClientSecurity();
    console.log('main.tsx: Security measures initialized');

    console.log('main.tsx: Initializing IndexedDB...');
    
    // Check and manage IndexedDB quota
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        if (estimate.usage && estimate.quota) {
          const percentUsed = (estimate.usage / estimate.quota) * 100;
          console.log(`IndexedDB: ${percentUsed.toFixed(1)}% used (${(estimate.usage / 1024 / 1024).toFixed(2)}MB / ${(estimate.quota / 1024 / 1024).toFixed(2)}MB)`);
          
          if (percentUsed > 80) {
            console.warn('⚠️ IndexedDB quota >80%, clearing old data');
            // Clear old cached data
            const dbs = await indexedDB.databases?.() || [];
            for (const db of dbs) {
              if (db.name && db.name.includes('cache')) {
                indexedDB.deleteDatabase(db.name);
              }
            }
          }
        }
      } catch (quotaError) {
        console.warn('Could not check IndexedDB quota:', quotaError);
      }
    }
    
    await indexedDBManager.init();
    console.log('main.tsx: IndexedDB initialized successfully');
  } catch (error) {
    console.warn('main.tsx: Initialization failed, continuing with limited functionality:', error);
  }
};

// Self-healing module loader with retry and cache clearing
async function loadAppWithRetry(maxRetries = 3): Promise<typeof import('./App.tsx')['default']> {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`main.tsx: Loading App (attempt ${attempt}/${maxRetries})...`);
      const module = await import('./App.tsx');
      return module.default;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      console.warn(`main.tsx: App load attempt ${attempt} failed:`, lastError.message);
      
      // On failure, try to clear caches and unregister service workers
      if (attempt < maxRetries) {
        try {
          // Clear all caches
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('main.tsx: Cleared all caches');
          }
          
          // Unregister service workers
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(reg => reg.unregister()));
            console.log('main.tsx: Unregistered all service workers');
          }
          
          // Wait before retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        } catch (cleanupErr) {
          console.warn('main.tsx: Cache cleanup failed:', cleanupErr);
        }
      }
    }
  }
  
  throw lastError || new Error('Failed to load App after retries');
}

// Render error UI with recovery options
function renderErrorUI(root: ReturnType<typeof createRoot>, error: Error) {
  const clearCacheAndReload = async () => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      window.location.href = window.location.pathname + '?nocache=' + Date.now();
    } catch (e) {
      window.location.reload();
    }
  };
  
  const openSafeMode = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('nosw', '1');
    url.searchParams.set('nocache', Date.now().toString());
    window.location.href = url.toString();
  };
  
  root.render(
    <div style={{padding: '20px', fontFamily: 'system-ui, Arial', maxWidth: '500px', margin: '40px auto', textAlign: 'center'}}>
      <h1 style={{color: '#dc2626', fontSize: '1.5rem', marginBottom: '16px'}}>🚨 Component Load Error</h1>
      <p style={{color: '#666', marginBottom: '24px', lineHeight: '1.5'}}>
        Failed to load application. This is usually caused by stale cached files.
      </p>
      <div style={{display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap'}}>
        <button 
          onClick={clearCacheAndReload}
          style={{padding: '12px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500'}}
        >
          Clear Cache & Reload
        </button>
        <button 
          onClick={openSafeMode}
          style={{padding: '12px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '500'}}
        >
          Open in Safe Mode
        </button>
      </div>
      <details style={{marginTop: '24px', textAlign: 'left', background: '#f9fafb', padding: '12px', borderRadius: '8px'}}>
        <summary style={{cursor: 'pointer', color: '#6b7280', fontSize: '0.875rem'}}>Technical details</summary>
        <pre style={{marginTop: '8px', fontSize: '0.75rem', color: '#ef4444', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
          {error.message}
        </pre>
      </details>
    </div>
  );
}

try {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error('main.tsx: Root element not found');
    throw new Error('Root element not found');
  }
  
  console.log('main.tsx: Root element found, creating React root');
  const root = createRoot(rootElement);
  
  // Mark JS bundle as loaded immediately so the watchdog doesn't kill us during slow imports
  ;(window as any).__APP_LOADED__ = true;
  console.log('main.tsx: __APP_LOADED__ flag set early (post-createRoot)');
  
  // Initialize IndexedDB and then render app with dynamic imports
  initializeApp().finally(async () => {
    try {
      const App = await loadAppWithRetry(3);

      console.log('main.tsx: Components loaded, rendering app...');
      root.render(<App />);
      
      console.log('main.tsx: App rendered successfully');
      
      // Strip stale nocache params from URL after a successful render
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('nocache')) {
          url.searchParams.delete('nocache');
          window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
          console.log('main.tsx: Stripped stale nocache param from URL');
        }
      } catch (urlErr) {
        console.warn('main.tsx: Could not clean URL:', urlErr);
      }
      
    } catch (error) {
      console.error('main.tsx: Failed to load components after all retries:', error);
      renderErrorUI(root, error instanceof Error ? error : new Error(String(error)));
    }
  });
  
  // Enhanced service worker registration with error handling
  try {
    serviceWorker.register({
      onSuccess: (registration) => {
        console.log('✅ App cached successfully for offline use', registration);
      },
      onUpdate: (registration) => {
        console.log('🔄 New app version available, please refresh', registration);
      },
    });
  } catch (swError) {
    console.warn('⚠️ Service Worker registration failed (non-critical):', swError);
  }
} catch (error) {
  console.error('main.tsx: Fatal error during app initialization:', error);
  document.body.innerHTML = `
    <div style="padding: 20px; color: red; font-family: Arial;">
      <h1>Fatal Application Error</h1>
      <p>The application failed to start. Check the console for details.</p>
      <p>Error: ${error}</p>
    </div>
  `;
}
